/**
 * BS-2151: per-tab anonymous session ID for OLA analytics.
 *
 * # Lifecycle
 *
 * - Generated lazily the first time `getSessionId()` is called.
 * - Persisted to `sessionStorage` so it survives soft reloads (the
 *   431-cookie reload path that `form-context.tsx` is built to
 *   survive) but resets when the tab closes — matching the per-tab
 *   funnel semantics PSA wants.
 * - Re-generated automatically if storage is unavailable (SSR,
 *   privacy mode) so the analytics emit path never throws.
 *
 * # Why sessionStorage, not localStorage
 *
 * The BS-2140 PII boundary decision is that each tab represents one
 * funnel attempt. Cross-tab consistency is explicitly out of scope
 * because the funnel-completion metric needs a clear answer to
 * "did THIS member finish THIS attempt"; smearing the session ID
 * across tabs would conflate parallel application flows.
 *
 * # Why this is NOT in `form-context`
 *
 * The form-context's `STORAGE_KEYS` are gated behind a schema-version
 * check and a one-time migration toast. The analytics session ID is
 * orthogonal: it has no schema, doesn't carry form state, and must be
 * available even when the form context decides to wipe everything
 * else for a migration. Keeping it in its own storage key lets us
 * avoid the migration entanglement.
 */

const SESSION_STORAGE_KEY = 'ola-analytics-session-id';

/**
 * RFC 4122 v4 UUID generator. Prefers the platform `crypto.randomUUID`
 * when present (all evergreen browsers including Safari 15.4+ have it)
 * and falls back to a math.random-seeded shape for the unlikely case
 * where it isn't available. The fallback is good enough for funnel
 * grouping — these UUIDs are not security tokens, they're correlation
 * keys for PSA's analytics rollups.
 */
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y is one of
  // 8, 9, a, or b. Not cryptographically strong; that's fine for a
  // correlation key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get (or lazily mint) the per-tab anonymous session ID. Returns a
 * fresh UUID on every call if `sessionStorage` is unavailable (SSR,
 * privacy mode, disabled storage) so the emit path never throws —
 * the analytics pipeline is allowed to drop coherence under those
 * conditions but is never allowed to break the form flow.
 */
export function getSessionId(): string {
  if (typeof window === 'undefined') {
    // SSR safety: don't mint a session ID server-side. The first
    // client-side caller will read sessionStorage and either pick up
    // an existing key or mint a new one. Returning a placeholder here
    // would risk caching a value that doesn't match the eventual
    // client session — better to require client-side resolution.
    return '';
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateUuid();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Storage unavailable (Safari private mode, quota error, etc.) —
    // generate a new UUID for this call. We accept that the events
    // emitted in this state won't correlate to a single session row;
    // the server will write them as one-event sessions. PSA's
    // funnel-completion metric degrades gracefully when this happens
    // (the "abandoned" bucket gets noisier) but the form still works.
    return generateUuid();
  }
}

/**
 * Clear the persisted session ID. Used by `resetForm` so the next
 * funnel attempt starts with a fresh anonymous session. Safe to call
 * during SSR (no-op).
 */
export function clearSessionId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Same fail-quiet rationale as getSessionId — analytics state
    // cleanup must not throw.
  }
}
